import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Duration (ms) of the smooth slow-down / speed-up transition on hover.
 */
const RATE_TRANSITION_MS = 500;

/**
 * Maximum allowed delta-time per animation frame (in seconds).
 * When a tab is backgrounded, requestAnimationFrame pauses — without this
 * clamp the next frame after resuming would jump forward by the full
 * background duration.
 */
const MAX_FRAME_DT = 0.1;

/**
 * Hard cap (ms) on how long we'll wait for images to load before measuring.
 * Prevents the marquee from being permanently broken if a single image stalls.
 */
const IMAGE_WAIT_TIMEOUT_MS = 2000;

/**
 * Smallest base-item width (px) we'll trust as a real measurement. Anything
 * below this means images haven't resolved their intrinsic size yet.
 */
const MIN_BASE_WIDTH = 8;

/**
 * Upper bound on cloned copies. Without it, a mis-measured base item can
 * produce hundreds of full-height clones and a catastrophic reflow.
 */
const MAX_COPIES = 50;

/**
 * A custom element that displays a marquee.
 *
 * The animation is driven by a JS requestAnimationFrame loop that writes
 * pixel-based transforms directly to the wrapper. The CSS still ships a
 * keyframe animation as a fallback (so the strip still moves if JS fails);
 * once JS takes over it explicitly disables the CSS animation by setting
 * animation: none inline on the wrapper.
 *
 * This rewrite fixes several Safari-specific issues from the previous version:
 *   - getAnimations()/updatePlaybackRate produced jittery / inconsistent speeds
 *   - the keyframe used calc(-50% - gap/2) on a fit-content flex wrapper,
 *     which Safari occasionally mis-composited (flicker / disappearing items)
 *   - measurement happened before images loaded, so numberOfCopies could be
 *     dramatically wrong on the first paint, which made the strip appear
 *     oversized in Safari
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /** @type {number | null} */
  #rafId = null;
  /** Last rAF timestamp (ms). */
  #lastTime = 0;
  /** Current scroll position (px) within one loop. Always in [0, loopDistance). */
  #position = 0;
  /** Distance (px) to translate before content visually loops back to start. */
  #loopDistance = 0;
  /** How fast the marquee moves at full playback rate (px/sec). */
  #pixelsPerSecond = 0;
  /** Current playback rate (0..1). 1 = full speed, 0 = paused. */
  #playbackRate = 1;
  /** Target rate the current rate is easing toward (1 by default, 0 on hover). */
  #targetPlaybackRate = 1;
  /** Cached marquee viewport width — used to skip vertical-only resizes. */
  #marqueeWidth = 0;
  /** 'normal' (content scrolls left) or 'reverse' (content scrolls right). */
  #direction = 'normal';
  /** True if user has prefers-reduced-motion: reduce. */
  #reducedMotion = false;
  /** Guards against re-entrant setup() calls during rapid resizes. */
  #setupRunning = false;
  /** ResizeObserver watching the host for layout-driven width changes. */
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** Width of the base item at the last successful setup. Used to skip
   *  redundant rebuilds, which perturb document height and let Chrome
   *  re-run scroll restoration / scroll anchoring. */
  #lastBaseWidth = 0;

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (!marqueeItems || marqueeItems.length === 0) return;

    this.#direction = this.getAttribute('data-movement-direction') || 'normal';
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Run an initial setup using whatever sizes are available right now —
    // this avoids the "stacked items / no layout" failure mode if image
    // loading hangs. We then re-run setup once images & fonts have settled
    // so the measurements (and therefore the loop point) are accurate.
    await this.#setup();
    if (this.#shouldAnimate()) this.#startAnimation();

    this.#waitForResources().then(() => {
      if (!this.isConnected) return;
      this.#redoSetup();
    });

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(this.#handleResize);
      this.#resizeObserver.observe(this);
    }

    window.addEventListener('resize', this.#handleResize);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopAnimation();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    window.removeEventListener('resize', this.#handleResize);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
  }

  #shouldAnimate() {
    return !this.#reducedMotion && !this.hasAttribute('data-disabled') && this.#loopDistance > 0;
  }

  /**
   * Wait until every <img> inside the marquee has either loaded or errored,
   * and until web fonts are ready — but bail out after IMAGE_WAIT_TIMEOUT_MS
   * so a single stalled image can't permanently break the strip.
   */
  #waitForResources() {
    const images = /** @type {HTMLImageElement[]} */ (Array.from(this.querySelectorAll('img')));
    const imagePromises = images.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => {
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          resolve(undefined);
        };
        img.addEventListener('load', done);
        img.addEventListener('error', done);
      });
    });

    /** @type {Promise<unknown>[]} */
    const promises = [...imagePromises];
    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      promises.push(document.fonts.ready.catch(() => {}));
    }

    const allDone = Promise.all(promises);
    const timeout = new Promise((resolve) => setTimeout(resolve, IMAGE_WAIT_TIMEOUT_MS));
    return Promise.race([allDone, timeout]);
  }

  /**
   * Redo setup after waiting for resources. Preserves the user's visual
   * scroll position by mapping the old fractional position into the new
   * loop distance.
   */
  async #redoSetup() {
    // If the base item measures the same as it did during the first setup,
    // a rebuild would produce an identical DOM. Skip it: every teardown +
    // rebuild momentarily changes this section's height, and on mobile that
    // is enough for Chrome to re-apply its pending scroll restoration.
    const baseItem = this.refs.marqueeItems?.[0];
    if (baseItem && this.#lastBaseWidth > 0) {
      const currentBaseWidth = baseItem.getBoundingClientRect().width;
      if (Math.abs(currentBaseWidth - this.#lastBaseWidth) < 1) return;
    }

    const wasAnimating = this.#rafId !== null;
    const oldPosition = this.#position;
    const oldLoop = this.#loopDistance;
    this.#stopAnimation();
    await this.#setup();
    if (oldLoop > 0 && this.#loopDistance > 0) {
      this.#position = (oldPosition / oldLoop) * this.#loopDistance;
      this.#applyTransform();
    }
    if (wasAnimating && this.#shouldAnimate()) this.#startAnimation();
  }

  /**
   * Build (or rebuild) the marquee:
   *   1. Reset wrapper & content to a single repeated-items block.
   *   2. Measure container width and one base item's width.
   *   3. Clone the base item enough times to fill the viewport.
   *   4. Append a single duplicate of the whole content block for seamless looping.
   *   5. Measure final loop distance and compute pixels-per-second.
   */
  async #setup() {
    if (this.#setupRunning) return;
    this.#setupRunning = true;

    try {
      const { content, wrapper, marqueeItems } = this.refs;
      const baseItem = marqueeItems[0];
      if (!baseItem) return;

      // Pin the host's height for the duration of the rebuild. Between the
      // teardown below and the re-clone further down, the strip briefly holds
      // a single item, which changes this section's height and therefore the
      // height of the whole document. On mobile Chrome that shift is what
      // lets scroll anchoring / scroll restoration move the viewport.
      const lockedHeight = this.getBoundingClientRect().height;
      if (lockedHeight > 0) this.style.height = `${lockedHeight}px`;

      // Reset: drop any previously appended clone of `content` from the wrapper
      while (wrapper.children.length > 1) {
        wrapper.lastElementChild?.remove();
      }
      // Reset: drop any previously appended copies of marquee-items from content
      while (content.children.length > 1) {
        content.lastElementChild?.remove();
      }

      this.#position = 0;
      // Disable the CSS keyframe fallback animation now that JS is in charge
      wrapper.style.animation = 'none';
      wrapper.style.transform = 'translate3d(0, 0, 0)';

      // NOTE: `void this.offsetWidth` used to sit here to force a layout flush.
      // It was redundant - getBoundingClientRect() on the next line flushes pending
      // style and layout itself, so the measurements below are identical either way.
      // Removing it drops one forced reflow per setup with no behaviour change.
      // The animation-restart trick still works: `animation: 'none'` above is
      // committed by this same flush before any later `animation = ''`.
      const containerWidth = this.getBoundingClientRect().width;
      const baseWidth = baseItem.getBoundingClientRect().width;
      this.#marqueeWidth = containerWidth;

      // A base item narrower than MIN_BASE_WIDTH means its images have no
      // intrinsic size yet. Measuring now would make numberOfCopies enormous
      // (see the clamp below) and trigger a huge, scroll-disturbing reflow.
      if (baseWidth < MIN_BASE_WIDTH || containerWidth <= 0) {
        // No useful sizes yet. Re-enable CSS fallback so the strip keeps
        // moving until ResizeObserver triggers another setup attempt.
        wrapper.style.animation = '';
        this.#loopDistance = 0;
        return;
      }

      // Add 1 extra copy so even non-multiple widths never expose a gap of
      // empty space at the right edge during scroll.
      this.#lastBaseWidth = baseWidth;

      const numberOfCopies = Math.min(
        MAX_COPIES,
        Math.max(1, Math.ceil(containerWidth / baseWidth) + 1)
      );

      const fragment = document.createDocumentFragment();
      for (let i = 1; i < numberOfCopies; i++) {
        const clonedItem = /** @type {HTMLElement} */ (baseItem.cloneNode(true));
        // Cloned copies are presentational only; strip refs so the
        // framework doesn't try to re-bind them.
        clonedItem.removeAttribute('ref');
        clonedItem.setAttribute('aria-hidden', 'true');
        fragment.appendChild(clonedItem);
      }
      content.appendChild(fragment);

      // NOTE: `void this.offsetWidth` used to sit here too. Also redundant -
      // getComputedStyle() and getBoundingClientRect() below both flush pending
      // layout, so the appended clones are measured exactly as before.

      // Loop distance = content width + the wrapper-level gap that separates
      // content from the soon-to-be-appended duplicate.
      const wrapperGap = parseFloat(getComputedStyle(wrapper).gap) || 0;
      const measuredContentWidth = content.getBoundingClientRect().width;

      if (measuredContentWidth <= 0) {
        wrapper.style.animation = '';
        this.#loopDistance = 0;
        return;
      }

      this.#loopDistance = measuredContentWidth + wrapperGap;

      // Preserve the original speed model: speed-factor seconds per loop,
      // scaled by sqrt(numberOfCopies).
      const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
      const speedSeconds = Math.max(0.0001, Math.sqrt(numberOfCopies) * speedFactor);
      this.#pixelsPerSecond = this.#loopDistance / speedSeconds;

      // Append the duplicate content block for seamless looping
      const clone = /** @type {HTMLElement} */ (content.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('ref');
      clone.querySelectorAll('[ref]').forEach((el) => el.removeAttribute('ref'));
      wrapper.appendChild(clone);

      // GPU layer hints — important for Safari smoothness
      wrapper.style.willChange = 'transform';
      wrapper.style.backfaceVisibility = 'hidden';
      // @ts-ignore -- vendor prefix
      wrapper.style.webkitBackfaceVisibility = 'hidden';
    } finally {
      this.style.height = '';
      this.#setupRunning = false;
    }
  }

  #startAnimation() {
    this.#stopAnimation();
    this.#lastTime = performance.now();
    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #stopAnimation() {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /**
   * Per-frame update: ease the playback rate toward its target, advance the
   * scroll position, write a new transform to the wrapper.
   *
   * @param {number} currentTime - High-res timestamp (ms).
   */
  #tick = (currentTime) => {
    const dt = Math.min((currentTime - this.#lastTime) / 1000, MAX_FRAME_DT);
    this.#lastTime = currentTime;

    if (this.#playbackRate !== this.#targetPlaybackRate) {
      const step = (dt * 1000) / RATE_TRANSITION_MS;
      if (this.#targetPlaybackRate > this.#playbackRate) {
        this.#playbackRate = Math.min(this.#playbackRate + step, this.#targetPlaybackRate);
      } else {
        this.#playbackRate = Math.max(this.#playbackRate - step, this.#targetPlaybackRate);
      }
    }

    if (this.#loopDistance > 0 && this.#playbackRate > 0) {
      this.#position += this.#pixelsPerSecond * this.#playbackRate * dt;
      if (this.#position >= this.#loopDistance) {
        this.#position %= this.#loopDistance;
      }
      this.#applyTransform();
    }

    this.#rafId = requestAnimationFrame(this.#tick);
  };

  /**
   * Translate the wrapper based on the current position and direction.
   *
   * For 'normal' direction the wrapper slides left (negative X). For 'reverse'
   * it slides right; we offset the starting transform by -loopDistance so the
   * clone is initially in the visible area, and rightward motion brings the
   * original content into view from the left edge — seamless when position wraps.
   */
  #applyTransform() {
    const x =
      this.#direction === 'reverse'
        ? -this.#loopDistance + this.#position
        : -this.#position;
    this.refs.wrapper.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0)`;
  }

  #onPointerEnter = () => {
    this.#targetPlaybackRate = 0;
  };

  #onPointerLeave = () => {
    this.#targetPlaybackRate = 1;
  };

  #handleVisibilityChange = () => {
    if (!document.hidden) this.#lastTime = performance.now();
  };

  #handleResize = debounce(async () => {
    const newWidth = this.getBoundingClientRect().width;
    if (Math.abs(newWidth - this.#marqueeWidth) < 1) return;

    this.#stopAnimation();
    await this.#setup();
    if (this.#shouldAnimate()) this.#startAnimation();
  }, 250);
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}